import React, { useState } from 'react';
import useSWR from 'swr';
import { adminListProducts, adminCreateProduct, adminUpdateProduct, adminDeleteProduct, uploadFileToStorage } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { Plus, Trash2, Edit2, Package, Search, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';

export default function AdminProducts() {
  const { data: products, mutate, isLoading, error } = useSWR('adminProducts', adminListProducts);
  const isQuotaExceeded = error?.code === 'resource-exhausted' || error?.message?.toLowerCase().includes('quota');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    title: '',
    brand: '',
    category: '',
    price: '',
    sale: '',
    image: '',
    images: [],
    gender: 'men',
    description: '',
    specs: ''
  });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEdit = (product) => {
    setEditingId(product.id);
    setFormData({
      title: product.title,
      brand: product.brand,
      category: product.category,
      price: product.price,
      sale: product.sale,
      image: product.image,
      images: product.images || [product.image],
      gender: product.gender || 'men',
      description: product.description || '',
      specs: product.specs || ''
    });
    setOpen(true);
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      title: '', brand: '', category: '', price: '', sale: '', image: '', images: [], gender: 'men', description: '', specs: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        price: Number(formData.price),
        sale: Number(formData.sale),
      };
      
      if (editingId) {
        await adminUpdateProduct(editingId, payload);
        toast({ title: 'Product updated successfully' });
      } else {
        await adminCreateProduct(payload);
        toast({ title: 'Product added successfully' });
      }
      
      setOpen(false);
      resetForm();
      mutate();
    } catch (err) {
      console.error('Product Save Error:', err);
      let msg = `Error ${editingId ? 'updating' : 'adding'} product`;
      if (err.code === 'resource-exhausted' || err.message?.includes('too large') || err.message?.includes('rate')) {
        msg = 'Request rate exceeded or data too large. Please wait a moment and try again.';
      }
      toast({ title: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const [uploading, setUploading] = useState(false);

  const uploadImage = async (file) => {
    // Check if image size exceeds 5MB
    if (file.size > 5 * 1024 * 1024) {
      toast({ 
        title: 'File is too large', 
        description: 'Please upload an image under 5MB.', 
        variant: 'destructive' 
      });
      return null;
    }
    
    try {
      const url = await uploadFileToStorage(file, 'products');
      toast({ title: 'Image uploaded successfully' });
      return url;
    } catch (err) {
      console.error('Image upload error:', err);
      toast({ title: 'Error uploading image', variant: 'destructive' });
      return null;
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = 10 - formData.images.length;
    const toUpload = files.slice(0, remaining);
    
    if (toUpload.length === 0) return;
    
    setUploading(true);
    for (const file of toUpload) {
      const url = await uploadImage(file);
      if (url) {
        setFormData(prev => {
          const newImages = [...prev.images, url];
          return { 
            ...prev, 
            images: newImages,
            image: prev.image || url
          };
        });
      }
    }
    setUploading(false);
  };

  const uniqueBrands = products ? [...new Set(products.map(p => p.brand))].filter(Boolean) : [];
  const uniqueCategories = products ? [...new Set(products.map(p => p.category))].filter(Boolean) : [];

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await adminDeleteProduct(id);
      toast({ title: 'Product deleted' });
      mutate();
    } catch (err) {
      toast({ title: 'Error deleting product', variant: 'destructive' });
    }
  };

  const filteredProducts = products?.filter(p => 
    p.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
    p.brand.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return <div className="p-6 space-y-4"><Skeleton className="h-10 w-48" /><Skeleton className="h-[400px] w-full" /></div>;
  }

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-serif text-neutral-900 mb-1">Products Management</h1>
          <p className="text-neutral-500 text-sm">Manage your inventory and product listings</p>
        </div>
        
        <Dialog open={open} onOpenChange={(val) => {
          setOpen(val);
          if (!val) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#b8892d] hover:bg-[#a67b28] text-white h-11 px-6">
              <Plus className="mr-2 h-5 w-5" /> Add New Product
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-serif">{editingId ? 'Edit Product' : 'Add New Product'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-5 py-4">
              <div className="space-y-1.5">
                <Label>Title</Label>
                <Input name="title" value={formData.title} onChange={handleChange} placeholder="e.g. Rolex Submariner Date" required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Brand</Label>
                  <div className="relative">
                    <Input name="brand" value={formData.brand} onChange={handleChange} placeholder="Rolex" required list="brands-list" />
                    <datalist id="brands-list">
                      {uniqueBrands.map(b => <option key={b} value={b} />)}
                    </datalist>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Category</Label>
                  <div className="relative">
                    <Input name="category" value={formData.category} onChange={handleChange} placeholder="Luxury" required list="categories-list" />
                    <datalist id="categories-list">
                      {uniqueCategories.map(c => <option key={c} value={c} />)}
                    </datalist>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Original Price (₹)</Label>
                  <Input type="number" name="price" value={formData.price} onChange={handleChange} required />
                </div>
                <div className="space-y-1.5">
                  <Label>Sale Price (₹)</Label>
                  <Input type="number" name="sale" value={formData.sale} onChange={handleChange} required />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Description</Label>
                <textarea 
                  name="description" 
                  value={formData.description} 
                  onChange={handleChange} 
                  placeholder="Detailed product description..."
                  className="w-full min-h-[80px] p-3 text-sm border border-neutral-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#b8892d]"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Key Specifications (One per line)</Label>
                <textarea 
                  name="specs" 
                  value={formData.specs} 
                  onChange={handleChange} 
                  placeholder="Swiss ETA Automatic Movement&#10;Sapphire Crystal Glass&#10;Water Resistant up to 100M"
                  className="w-full min-h-[80px] p-3 text-sm border border-neutral-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#b8892d]"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Product Images (Up to 10)</Label>
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {formData.images.map((img, idx) => (
                      <div key={idx} className="relative group w-20 h-20 border border-neutral-200 rounded-md overflow-hidden bg-neutral-50 shadow-sm">
                        <img src={img} alt={`Preview ${idx + 1}`} className="w-full h-full object-cover" />
                        <button 
                          type="button"
                          onClick={() => {
                            const newImages = formData.images.filter((_, i) => i !== idx);
                            setFormData(prev => ({ 
                              ...prev, 
                              images: newImages,
                              image: newImages.length > 0 ? newImages[0] : ''
                            }));
                          }}
                          className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white text-[10px] font-bold"
                        >
                          Remove
                        </button>
                        {idx === 0 && (
                          <div className="absolute bottom-0 left-0 right-0 bg-[#b8892d] text-white text-[8px] text-center font-bold py-0.5">COVER</div>
                        )}
                      </div>
                    ))}
                    {formData.images.length < 10 && (
                      <div className="relative w-20 h-20 border-2 border-dashed border-neutral-200 rounded-md flex flex-col items-center justify-center bg-neutral-50 hover:bg-neutral-100 hover:border-[#b8892d]/50 transition-all cursor-pointer">
                        {uploading ? (
                          <Loader2 className="w-5 h-5 text-neutral-400 animate-spin" />
                        ) : (
                          <>
                            <input 
                              type="file" 
                              multiple 
                              accept="image/*" 
                              onChange={handleFileUpload} 
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            />
                            <Plus className="w-5 h-5 text-neutral-400" />
                            <span className="text-[9px] text-neutral-400 mt-1 font-bold">ADD IMG</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <p className="text-[10px] text-neutral-400 font-medium">Click "ADD IMG" to upload. First image is the cover. Max 10 images.</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Gender</Label>
                <Select value={formData.gender} onValueChange={(val) => handleSelectChange('gender', val)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="men">Men</SelectItem>
                    <SelectItem value="women">Women</SelectItem>
                    <SelectItem value="unisex">Unisex</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full bg-[#b8892d] hover:bg-[#a67b28] text-white h-11" disabled={loading}>
                {loading ? (editingId ? 'Updating...' : 'Adding...') : (editingId ? 'Update Product' : 'Add Product')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isQuotaExceeded && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-lg mb-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <p className="text-sm font-medium">Daily request limit reached. As you've upgraded to Blaze, this should clear in a few minutes.</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => mutate()} className="text-amber-800 hover:bg-amber-100">Retry</Button>
        </div>
      )}

      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-neutral-200 bg-neutral-50/50 flex flex-col md:flex-row gap-4 items-center">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-neutral-400" />
            <Input 
              placeholder="Search products by title, brand, category..." 
              className="pl-10 h-10 border-neutral-200"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2 text-sm text-neutral-500 whitespace-nowrap">
            Showing <span className="font-semibold text-neutral-900">{filteredProducts?.length || 0}</span> products
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-neutral-50 text-neutral-500 font-medium border-b border-neutral-200">
              <tr>
                <th className="px-6 py-4">Product Details</th>
                <th className="px-6 py-4">Category</th>
                <th className="px-6 py-4">Price</th>
                <th className="px-6 py-4">Gender</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {filteredProducts?.map((product) => (
                <tr key={product.id} className="hover:bg-neutral-50/80 transition-colors group">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-neutral-100 rounded-lg overflow-hidden flex-shrink-0 border border-neutral-100">
                        <img src={product.image} alt={product.title} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <div className="font-bold text-neutral-900 line-clamp-1">{product.title}</div>
                        <div className="text-xs text-neutral-500">{product.brand}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant="outline" className="bg-white font-normal text-neutral-600">{product.category}</Badge>
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-bold text-neutral-900">₹{product.sale.toLocaleString()}</div>
                    <div className="text-[10px] line-through text-neutral-400 uppercase tracking-tighter">₹{product.price.toLocaleString()}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="capitalize px-2.5 py-1 rounded-full bg-neutral-100 text-neutral-600 text-[10px] font-bold">
                      {product.gender}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(product)} className="h-8 w-8 p-0 text-neutral-600 hover:text-[#b8892d] hover:border-[#b8892d]/30">
                        <Edit2 className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(product.id)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 hover:border-red-100">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredProducts?.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-20 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <Package className="h-12 w-12 text-neutral-200 mb-3" />
                      <p className="text-neutral-500 text-base">No products found matching your search.</p>
                      {searchQuery && (
                        <Button variant="link" onClick={() => setSearchQuery('')} className="text-[#b8892d] mt-1">Clear search</Button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

